import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUI } from '../../contexts/UIContext';
import {
  KeyIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import MFADisableSupportForm from '../../components/support/MFADisableSupportForm';
import { apiBaseUrl } from '../../config/environment';

const UserSecuritySettings = () => {
  const { changePassword, session } = useAuth();
  const { showNotification } = useUI();
  
  // Password state
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // 2FA state
  const [mfaFactors, setMfaFactors] = useState([]);
  const [loading2FA, setLoading2FA] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  
  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [terminatingSession, setTerminatingSession] = useState(null);
  
  // Audit log state
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAuditLog, setLoadingAuditLog] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  
  // Support form state for MFA disable requests
  const [showMFASupportForm, setShowMFASupportForm] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showNotification('New passwords do not match', 'error');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      showNotification('Password must be at least 8 characters long', 'error');
      return;
    }

    try {
      setChangingPassword(true);
      await changePassword(passwordForm.newPassword);

      showNotification('Password changed successfully', 'success');
      setShowPasswordForm(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      showNotification('Failed to change password', 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Load initial data
  useEffect(() => {
    if (session?.access_token) {
      loadMFAFactors();
      loadSessions();
    }
  }, [session]);

  // API functions for 2FA
  const loadMFAFactors = async () => {
    try {
      setLoading2FA(true);
      const token = session?.access_token;
      
      if (!token) {
        console.error('No access token available');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/security/mfa/factors`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMfaFactors(data.factors || []);
      } else if (response.status === 401) {
        const errorData = await response.json().catch(() => ({}));
        showNotification(errorData.error || 'Session expired - please log out and log back in', 'error');
      }
    } catch (error) {
      console.error('Error loading MFA factors:', error);
    } finally {
      setLoading2FA(false);
    }
  };

  const enroll2FA = async () => {
    try {
      setLoading2FA(true);
      const token = session?.access_token;
      
      if (!token) {
        showNotification('Authentication required', 'error');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/security/mfa/enroll`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'totp',
          friendly_name: 'Authenticator App'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setQrCode(data.qr_code);
        setTotpSecret(data.secret);
        setFactorId(data.factor_id);
        setShow2FASetup(true);
      } else if (response.status === 401) {
        const errorData = await response.json().catch(() => ({}));
        showNotification(errorData.error || 'Session expired - please log out and log back in', 'error');
      } else if (response.status === 409) {
        // MFA already enabled
        const errorData = await response.json();
        showNotification(errorData.message || 'Two-factor authentication is already enabled', 'warning');
        await loadMFAFactors(); // Refresh the factors list
      } else {
        const errorData = await response.json().catch(() => ({}));
        showNotification(errorData.message || 'Failed to start 2FA setup', 'error');
      }
    } catch (error) {
      console.error('Error enrolling 2FA:', error);
      showNotification('Error setting up 2FA', 'error');
    } finally {
      setLoading2FA(false);
    }
  };

  const verify2FA = async () => {
    try {
      setVerifying2FA(true);
      const token = session?.access_token;
      
      if (!token) {
        showNotification('Authentication required', 'error');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/security/mfa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          factor_id: factorId,
          code: verificationCode
        })
      });
      
      if (response.ok) {
        showNotification('Two-factor authentication enabled successfully!', 'success');
        setShow2FASetup(false);
        setVerificationCode('');
        await loadMFAFactors();
      } else {
        showNotification('Invalid verification code', 'error');
      }
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      showNotification('Error verifying code', 'error');
    } finally {
      setVerifying2FA(false);
    }
  };

  const disable2FA = async (factorId) => {
    try {
      setLoading2FA(true);
      const token = session?.access_token;
      
      if (!token) {
        showNotification('Authentication required', 'error');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/security/mfa/unenroll/${factorId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        showNotification('Two-factor authentication disabled', 'success');
        await loadMFAFactors();
      } else {
        const errorData = await response.json().catch(() => ({}));
        
        // Check if this is the MFA unenroll limitation
        if (errorData.error_code === 'mfa_unenroll_not_supported' || errorData.support_required) {
          // Open the support form pre-filled for MFA disable request
          setShowMFASupportForm(true);
          showNotification('Please use the support form to request MFA removal', 'info');
        } else {
          showNotification(errorData.message || 'Failed to disable 2FA', 'error');
        }
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      showNotification('Error disabling 2FA', 'error');
    } finally {
      setLoading2FA(false);
    }
  };

  // API functions for session management
  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      const token = session?.access_token;
      
      if (!token) {
        console.error('No access token available for loading sessions');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/security/sessions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const terminateSession = async (sessionId) => {
    try {
      setTerminatingSession(sessionId);
      const token = session?.access_token;
      
      if (!token) {
        showNotification('Authentication required', 'error');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/security/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        showNotification('Session terminated', 'success');
        await loadSessions();
      } else {
        showNotification('Failed to terminate session', 'error');
      }
    } catch (error) {
      console.error('Error terminating session:', error);
      showNotification('Error terminating session', 'error');
    } finally {
      setTerminatingSession(null);
    }
  };

  const terminateAllOtherSessions = async () => {
    try {
      setLoadingSessions(true);
      const token = session?.access_token;
      
      if (!token) {
        showNotification('Authentication required', 'error');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/security/sessions/others`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        showNotification(data.message, 'success');
        await loadSessions();
      } else {
        showNotification('Failed to terminate sessions', 'error');
      }
    } catch (error) {
      console.error('Error terminating sessions:', error);
      showNotification('Error terminating sessions', 'error');
    } finally {
      setLoadingSessions(false);
    }
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 1) return 'Now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return DevicePhoneMobileIcon;
      case 'tablet':
        return DevicePhoneMobileIcon;
      case 'desktop':
      default:
        return DevicePhoneMobileIcon;
    }
  };

  // API function for audit log
  const loadAuditLog = async () => {
    try {
      setLoadingAuditLog(true);
      const token = session?.access_token;
      
      if (!token) {
        console.error('No access token available for loading audit log');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/security/audit-log?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuditLog(data.events || []);
      }
    } catch (error) {
      console.error('Error loading audit log:', error);
    } finally {
      setLoadingAuditLog(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Password Section */}
      <div className="border-b border-secondary-200 pb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <KeyIcon className="h-5 w-5 text-secondary-400 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-secondary-900">Password</h3>
              <p className="text-sm text-secondary-500">Manage your account password</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="inline-flex items-center px-3 py-2 border border-secondary-300 shadow-sm text-sm leading-4 font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {showPasswordForm ? 'Cancel' : 'Change Password'}
          </button>
        </div>

        {showPasswordForm && (
          <form onSubmit={handlePasswordChange} className="space-y-4 ml-8">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                className="block w-full max-w-md border-secondary-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                className="block w-full max-w-md border-secondary-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                required
                minLength={8}
              />
              <p className="mt-1 text-xs text-secondary-500">
                Password must be at least 8 characters long
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                className="block w-full max-w-md border-secondary-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                required
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={changingPassword}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {changingPassword ? (
                  <>
                    <LoadingSpinner size="xs" className="mr-2" />
                    Changing...
                  </>
                ) : (
                  'Change Password'
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowPasswordForm(false)}
                className="inline-flex items-center px-4 py-2 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Two-Factor Authentication */}
      <div className="border-b border-secondary-200 pb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-5 w-5 text-secondary-400 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-secondary-900">Two-Factor Authentication</h3>
              <p className="text-sm text-secondary-500">Add an extra layer of security to your account</p>
            </div>
          </div>
          <div className="flex items-center">
            {loading2FA ? (
              <LoadingSpinner size="xs" className="mr-3" />
            ) : (
              <>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-3 ${
                  mfaFactors.length > 0 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-secondary-100 text-secondary-800'
                }`}>
                  {mfaFactors.length > 0 ? 'Enabled' : 'Not Enabled'}
                </span>
                {mfaFactors.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => disable2FA(mfaFactors[0].id)}
                    disabled={loading2FA}
                    className="inline-flex items-center px-3 py-2 border border-danger-300 shadow-sm text-sm leading-4 font-medium rounded-md text-danger-700 bg-white hover:bg-danger-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-500"
                    title="Request manual assistance to disable two-factor authentication"
                  >
                    Request MFA Removal
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={enroll2FA}
                    disabled={loading2FA}
                    className="inline-flex items-center px-3 py-2 border border-secondary-300 shadow-sm text-sm leading-4 font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Setup 2FA
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="ml-8">
          {mfaFactors.length > 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckIcon className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Two-Factor Authentication Enabled</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>Your account is protected with two-factor authentication. You'll need your authenticator app to sign in.</p>
                    <p className="mt-1 text-xs text-green-600">
                      Note: Disabling MFA requires manual verification by our support team for security reasons.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ShieldCheckIcon className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Why enable 2FA?</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>Two-factor authentication adds an extra layer of security by requiring a second form of verification when signing in.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 2FA Setup Modal */}
      {show2FASetup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-secondary-900">Setup Two-Factor Authentication</h3>
              <button
                onClick={() => {
                  setShow2FASetup(false);
                  setVerificationCode('');
                }}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-secondary-600 mb-4">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):
                </p>
                
                {qrCode && (
                  <div className="flex justify-center mb-4">
                    {qrCode.startsWith('data:image/png;base64,') ? (
                      <img src={qrCode} alt="2FA QR Code" className="border rounded-lg" />
                    ) : qrCode.startsWith('<?xml') || qrCode.startsWith('<svg') ? (
                      <div 
                        className="border rounded-lg" 
                        dangerouslySetInnerHTML={{ __html: qrCode }}
                      />
                    ) : (
                      <img src={qrCode} alt="2FA QR Code" className="border rounded-lg" />
                    )}
                  </div>
                )}

                <div className="bg-secondary-100 p-3 rounded-md mb-4">
                  <p className="text-xs text-secondary-600 mb-2">Manual entry key:</p>
                  <code className="text-sm font-mono break-all">{totpSecret}</code>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Enter verification code from your app:
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="000000"
                  className="block w-full border-secondary-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm text-center"
                  maxLength={6}
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={verify2FA}
                  disabled={verifying2FA || verificationCode.length !== 6}
                  className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {verifying2FA ? (
                    <>
                      <LoadingSpinner size="xs" className="mr-2" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Enable'
                  )}
                </button>
                <button
                  onClick={() => {
                    setShow2FASetup(false);
                    setVerificationCode('');
                  }}
                  className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Session Management */}
      <div className="border-b border-secondary-200 pb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <ClockIcon className="h-5 w-5 text-secondary-400 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-secondary-900">Active Sessions</h3>
              <p className="text-sm text-secondary-500">Manage where you're signed in</p>
            </div>
          </div>
          {loadingSessions && <LoadingSpinner size="xs" />}
        </div>

        <div className="ml-8 space-y-4">
          {sessions.length === 0 ? (
            <div className="text-center py-8 text-secondary-500">
              <ClockIcon className="h-12 w-12 mx-auto mb-4 text-secondary-300" />
              <p>No active sessions found</p>
            </div>
          ) : (
            sessions.map((session) => {
              const DeviceIcon = getDeviceIcon(session.device_type);
              return (
                <div key={session.id} className="flex items-center justify-between p-4 border border-secondary-200 rounded-lg">
                  <div className="flex items-center">
                    <DeviceIcon className="h-8 w-8 text-secondary-400 mr-3" />
                    <div>
                      <h4 className="text-sm font-medium text-secondary-900">
                        {session.device_type} - {session.browser}
                      </h4>
                      <p className="text-sm text-secondary-500">
                        {session.os} • Last active: {formatRelativeTime(session.last_active)}
                      </p>
                      <p className="text-xs text-secondary-400">
                        IP: {session.ip_address} • Location: {session.location}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {session.is_current ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Current
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => terminateSession(session.id)}
                        disabled={terminatingSession === session.id}
                        className="inline-flex items-center px-3 py-2 border border-secondary-300 shadow-sm text-sm leading-4 font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                      >
                        {terminatingSession === session.id ? (
                          <>
                            <LoadingSpinner size="xs" className="mr-1" />
                            Signing Out...
                          </>
                        ) : (
                          'Sign Out'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {sessions.length > 1 && (
            <div className="pt-4">
              <button
                type="button"
                onClick={terminateAllOtherSessions}
                disabled={loadingSessions}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-danger-700 bg-danger-100 hover:bg-danger-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-500 disabled:opacity-50"
              >
                {loadingSessions ? (
                  <>
                    <LoadingSpinner size="xs" className="mr-2" />
                    Signing Out...
                  </>
                ) : (
                  <>
                    <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                    Sign Out All Other Sessions
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Security Audit Log */}
      <div className="border-b border-secondary-200 pb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-secondary-400 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-secondary-900">Security Activity</h3>
              <p className="text-sm text-secondary-500">Recent security events for your account</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setShowAuditLog(!showAuditLog);
              if (!showAuditLog && auditLog.length === 0) {
                loadAuditLog();
              }
            }}
            className="inline-flex items-center px-3 py-2 border border-secondary-300 shadow-sm text-sm leading-4 font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {showAuditLog ? 'Hide Activity' : 'Show Activity'}
          </button>
        </div>

        {showAuditLog && (
          <div className="ml-8">
            {loadingAuditLog ? (
              <div className="text-center py-8">
                <LoadingSpinner size="sm" />
                <p className="mt-2 text-sm text-secondary-500">Loading security activity...</p>
              </div>
            ) : auditLog.length === 0 ? (
              <div className="text-center py-8 text-secondary-500">
                <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-4 text-secondary-300" />
                <p>No security activity found</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {auditLog.map((event) => (
                  <div key={event.id} className="flex items-start space-x-3 p-3 bg-secondary-50 rounded-lg">
                    <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                      event.severity === 'high' ? 'bg-red-500' :
                      event.severity === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-secondary-900">
                          {event.description}
                        </p>
                        <p className="text-xs text-secondary-500">
                          {formatRelativeTime(event.created_at)}
                        </p>
                      </div>
                      <div className="mt-1 text-xs text-secondary-500">
                        <span className="inline-flex items-center space-x-2">
                          <span>IP: {event.ip_address || 'Unknown'}</span>
                          {event.location && (
                            <>
                              <span>•</span>
                              <span>{event.location}</span>
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Account Deletion */}
      <div>
        <div className="flex items-center mb-4">
          <ExclamationTriangleIcon className="h-5 w-5 text-danger-400 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-secondary-900">Danger Zone</h3>
            <p className="text-sm text-secondary-500">Irreversible actions for your account</p>
          </div>
        </div>

        <div className="ml-8">
          <div className="bg-danger-50 border border-danger-200 rounded-md p-4">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="text-sm font-medium text-danger-800">Delete Account</h4>
                <p className="text-sm text-danger-700 mt-1">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
              </div>
              <button
                type="button"
                className="inline-flex items-center px-3 py-2 border border-danger-300 shadow-sm text-sm leading-4 font-medium rounded-md text-danger-700 bg-white hover:bg-danger-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-500"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MFA Disable Support Form */}
      <MFADisableSupportForm 
        isOpen={showMFASupportForm} 
        onClose={() => setShowMFASupportForm(false)}
      />
    </div>
  );
};

export default UserSecuritySettings;